import type { NavSectionId } from "../../shell/nav-items"

/**
 * Тексты заглушки раздела («Заглушка» в макете «Мастер, доступ и заглушка — П1»).
 *
 * ЗАГЛУШКА ОДНА НА ВСЕ РАЗДЕЛЫ, но текст у каждого свой, и это не украшение. Человек,
 * дошедший до пустого раздела, задаёт два вопроса: «что здесь будет» и «где смотреть
 * это сейчас». Общая фраза «раздел в разработке» не отвечает ни на один — и он звонит
 * в поддержку либо считает, что программа недоделана целиком.
 *
 * СЛУЖЕБНОГО АДРЕСА ЗДЕСЬ НЕТ. Раньше экран печатал свой путь («/reports») — это адрес
 * для того, кто пишет код, а не для владельца салона: в разговоре он его не назовёт,
 * а доверия к экрану строка не добавляет.
 *
 * ДЕЙСТВИЕ ВЕДЁТ ТУДА, ГДЕ ЭТО УЖЕ РАБОТАЕТ, и появляется только когда такое место
 * действительно есть. Кнопка «Написать в Telegram» не показывается, пока владелец
 * не назвал контакты поддержки: обещать адрес, которого нет, хуже, чем не обещать.
 */
export interface PlaceholderText {
  readonly head: string
  readonly text: string
  /** Куда идти сейчас. null — идти пока некуда, и врать об этом нельзя. */
  readonly action: { readonly label: string; readonly to: string } | null
}

const TEXTS: Partial<Record<NavSectionId, PlaceholderText>> = {
  reports: {
    head: "Отчёты появятся в следующем обновлении",
    text: "Выручку и загрузку за сегодня показывает сводка. Здесь будут выручка по периодам, загрузка и возвращаемость.",
    // В сводку, а не в журнал: кнопка названа «Открыть сводку», и раздел сводки есть
    // (5-В1). Подпись, ведущая не туда, учит не верить остальным кнопкам.
    action: { label: "Открыть сводку", to: "/summary" },
  },
  campaigns: {
    head: "Рассылки появятся в следующем обновлении",
    text: "Рекламные сообщения уйдут только клиентам с отдельным согласием на рекламу — журнал согласий уже ведётся в карточках.",
    action: { label: "К согласиям клиентов", to: "/clients" },
  },
  audit: {
    head: "Журнал действий появится в следующем обновлении",
    text: "Кто открывал телефоны, кто менял запись, кто выгружал базу. Пока — отметки в истории каждой записи.",
    action: { label: "В журнал записи", to: "/journal" },
  },
  support: {
    head: "Обращения в поддержку появятся в следующем обновлении",
    // Адрес почты и Telegram называет владелец (вопрос 13 в списке решений). До этого
    // экран не даёт контакта: выдуманный адрес хуже отсутствующего.
    text: "Пока напишите тому, кто подключал вам Planerius. Свои контакты поддержки мы добавим сюда, как только они будут утверждены.",
    action: null,
  },
}

export function placeholderText(id: NavSectionId, label: string): PlaceholderText {
  return (
    TEXTS[id] ?? {
      head: `${label} появятся в следующем обновлении`,
      text: "Раздел заведён в меню заранее, чтобы оно не перестраивалось потом. Остальные разделы работают.",
      action: null,
    }
  )
}
